import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import DateRangeIcon from '@mui/icons-material/DateRange'
import Grid from '@mui/material/Grid2'
import CaleoDatePicker from 'Components/reusable/InputFields/CaleoDatePicker'
import { useContext } from 'react'
import { DatePickerProps } from 'react-datepicker'
import { formatEndDate, formatStartDate, getDateFormatByLocaleAndResolution } from 'utils/utils'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType, getFieldControlId } from '../types'

/**
 * Date field (with label/outlined variant).
 */
export const DataEditContextDateField = <T extends AnyObject>({
  id,
  field,
  dateResolution,
  label,
  start,
  end,
  minDate,
  isClearable,
  insetLabel,
  required = false,
  withTime = false,
}: {
  id?: string
  field: EditableFieldsOfType<T, Date | null>
  dateResolution?: 'day' | 'month' | 'year'
  label?: string
  minDate?: Date
  start?: boolean
  end?: boolean
  isClearable?: boolean
  insetLabel?: boolean
  required?: boolean
  withTime?: boolean
} & Omit<DatePickerProps, 'onChange' | 'dateResolution'>) => {
  const { flatData, disabled, errors, setFields, localeBase, t, i18n } = useContext<ContextValue<T>>(Context)
  const controlId = getFieldControlId(field, id)

  const dateResolutionValue =
    dateResolution ??
    ((flatData as Record<string, unknown>)['dateResolution'] as 'day' | 'month' | 'year' | undefined) ??
    'month'

  const handleChange = (newValue: Date | null) => {
    const alteredDate = newValue
    if (alteredDate !== null && !withTime) {
      if (start) {
        formatStartDate(alteredDate, dateResolutionValue)
      } else if (end) {
        formatEndDate(alteredDate, dateResolutionValue)
      }
    }
    setFields({ [field]: alteredDate })
  }

  const value = flatData[field] as unknown as Date | null

  let error = errors[field]

  if (!error && value && minDate) {
    if ((dateResolutionValue === 'day' || !dateResolutionValue) && value < minDate) {
      error = i18n.t('startDateEndDateError')
    }
    if (dateResolutionValue === 'month') {
      const monthValue = new Date(value)
      monthValue.setDate(1)
      const earliestSelectableMonth = new Date(minDate)
      earliestSelectableMonth.setDate(1)
      if (monthValue < earliestSelectableMonth) error = i18n.t('startDateEndDateError')
    }
    if (dateResolutionValue === 'year' && value < minDate) {
      const yearValue = new Date(value)
      yearValue.setDate(1)
      yearValue.setMonth(0)
      const earliestSelectableYear = new Date(minDate)
      earliestSelectableYear.setDate(1)
      earliestSelectableYear.setMonth(0)
      if (yearValue < earliestSelectableYear) error = i18n.t('startDateEndDateError')
    }
  }

  const locale = i18n.language
  const format = getDateFormatByLocaleAndResolution(locale, dateResolutionValue)

  const handleClear = () => {
    setFields({ [field]: null })
  }

  const labelContent = label ? t(label) : t(`${localeBase}.${field}`)

  return (
    <CaleoDatePicker
      id={controlId}
      selected={typeof value === 'string' ? new Date(value) : value}
      onChange={handleChange}
      dateFormat={format}
      showMonthYearPicker={dateResolutionValue === 'month'}
      showYearPicker={dateResolutionValue === 'year'}
      disabled={disabled[field]}
      minDate={minDate}
      customInput={
        <TextField
          data-testid={`field-date-picker-${controlId}`}
          margin="dense"
          error={!!error}
          label={
            insetLabel ? (
              <Grid pr={labelContent && typeof labelContent === 'string' && labelContent.length > 15 ? 1.5 : 0.4}>
                {labelContent}
                {required && (
                  <Typography color="#BE3C3C" fontSize="0.85em" fontWeight="bold" component="span" pl={0.5} pr={0.5}>
                    {required && `${t('required')}`}
                  </Typography>
                )}
              </Grid>
            ) : undefined
          }
          variant="outlined"
          helperText={error ? error : undefined}
          size="small"
          required={required}
          disabled={disabled[field]}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {isClearable && value && (
                    <IconButton
                      data-testid={`button-date-field-clear-${controlId}`}
                      onClick={handleClear}
                      onMouseDown={handleClear}
                      size="small"
                      sx={{ p: 0, m: 0 }}
                    >
                      <CloseIcon style={{ cursor: 'pointer' }} fontSize="small" />
                    </IconButton>
                  )}
                  <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                </InputAdornment>
              ),
            },
            inputLabel: insetLabel ? { shrink: true } : {},
            formHelperText: { style: { textAlign: 'right' } },
          }}
        />
      }
      popperPlacement="bottom-start"
      showMonthDropdown
      useShortMonthInDropdown
      showYearDropdown
      withTime={withTime}
    />
  )
}

/**
 * Date picker field (standard variant, no label).
 */
